import { decode } from "@msgpack/msgpack";
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { signalState, type VehicleAspect } from "../simulation/signals";
import {
  STREET_LIGHT_STRIDE,
  TRAFFIC_SIGNAL_STRIDE,
  decodeCityLightingTile,
  validateCityLightingManifest,
  type CityLightingManifest,
  type CityLightingTileIndexEntry,
  type DecodedCityLightingTile,
} from "../world/lighting-schema";
import { roundedBox, standardMaterial } from "./materials";

export const MAX_LOCAL_STREET_LIGHTS = 12 as const;
export const MAX_LIGHTING_BATCHES_PER_TILE = 9 as const;
const LOCAL_LIGHT_SELECTION_RADIUS_M = 96;
const MAX_RENDERED_LOCAL_STREET_LIGHTS = 4;
const LOCAL_LIGHT_RANGE_M = 48;
const LOCAL_LIGHT_REFRESH_DISTANCE_M = 4;
const STREET_LIGHT_BASE_HEIGHT_M = 7.2;
const SIGNAL_COLORS = { red: 0xc84e45, yellow: 0xe2b83f, green: 0x3cab66 } as const;
const ASPECTS = ["red", "yellow", "green"] as const satisfies readonly VehicleAspect[];

export interface CityLightingSnapshot {
  artificialLight: number;
  streetLights: number;
  activeLocalLights: number;
  shadowCastingStreetLights: number;
  trafficSignals: number;
  signalAspects: { red: number; yellow: number; green: number };
  batches: number;
}

interface StreetLightRecord {
  x: number;
  z: number;
  yawRadians: number;
  heightM: number;
}
interface TrafficSignalRecord {
  x: number;
  z: number;
  yawRadians: number;
  axis: 0 | 1;
  crosswalkLengthM: number;
}
interface SignalMeshes {
  active: Record<VehicleAspect, THREE.InstancedMesh>;
  inactive: Record<VehicleAspect, THREE.InstancedMesh>;
}
interface LightingTile {
  root: THREE.Group;
  streetLights: StreetLightRecord[];
  trafficSignals: TrafficSignalRecord[];
  signalMeshes?: SignalMeshes;
  batches: number;
}
export interface PreparedLightingTile {
  readonly id: string;
  readonly generation: number;
  readonly root: THREE.Group;
}

export interface RetiredLightingTile {
  readonly id: string;
}

interface PreparedLightingRecord {
  handle: PreparedLightingTile;
  tile: LightingTile;
}

function mergedRoundedBoxes(
  parts: readonly {
    size: readonly [number, number, number];
    position: readonly [number, number, number];
  }[],
): THREE.BufferGeometry {
  const geometries = parts.map(({ size, position }) => {
    const geometry = roundedBox(...size, Math.min(...size) / 4);
    geometry.translate(...position);
    return geometry;
  });
  try {
    const merged = mergeGeometries(geometries, false);
    if (!merged) throw new Error("Failed to merge city-lighting geometry");
    return merged;
  } finally {
    for (const geometry of geometries) geometry.dispose();
  }
}

function setTransform(
  mesh: THREE.InstancedMesh,
  index: number,
  object: THREE.Object3D,
  x: number,
  z: number,
  yawRadians: number,
  scaleY = 1,
): void {
  object.position.set(x, 0, z);
  object.rotation.set(0, yawRadians, 0);
  object.scale.set(1, scaleY, 1);
  object.updateMatrix();
  mesh.setMatrixAt(index, object.matrix);
}

function finishInstances(mesh: THREE.InstancedMesh): void {
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingBox();
  mesh.computeBoundingSphere();
}

export class CityLightingSystem {
  readonly #entries: ReadonlyMap<string, CityLightingTileIndexEntry>;
  readonly #tiles = new Map<string, LightingTile>();
  readonly #preparedTiles = new Map<string, PreparedLightingRecord>();
  readonly #retiredTiles = new Map<RetiredLightingTile, LightingTile>();
  readonly #frameMaterial = standardMaterial(0x253236, 0.72, 0.42);
  readonly #lampMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd69a,
    emissive: 0xffd69a,
    emissiveIntensity: 0,
    roughness: 0.5,
    metalness: 0,
  });
  readonly #signalMaterials: {
    active: Record<VehicleAspect, THREE.MeshStandardMaterial>;
    inactive: Record<VehicleAspect, THREE.MeshStandardMaterial>;
  };
  readonly #streetFrameGeometry = mergedRoundedBoxes([
    {
      size: [0.16, STREET_LIGHT_BASE_HEIGHT_M, 0.16],
      position: [0, STREET_LIGHT_BASE_HEIGHT_M / 2, 0],
    },
    { size: [1.1, 0.16, 0.16], position: [0.55, STREET_LIGHT_BASE_HEIGHT_M - 0.2, 0] },
    { size: [0.72, 0.2, 0.34], position: [1.1, STREET_LIGHT_BASE_HEIGHT_M - 0.2, 0] },
  ]);
  readonly #lampLensGeometry = roundedBox(0.52, 0.06, 0.22, 0.015).translate(
    1.1,
    STREET_LIGHT_BASE_HEIGHT_M - 0.33,
    0,
  );
  readonly #signalFrameGeometry = mergedRoundedBoxes([
    { size: [0.16, 4.2, 0.16], position: [0, 2.1, 0] },
    { size: [0.42, 1.15, 0.28], position: [0, 3.35, 0] },
  ]);
  readonly #signalLensGeometries: Record<VehicleAspect, THREE.BufferGeometry> = {
    red: roundedBox(0.22, 0.22, 0.08, 0.04).translate(0, 3.68, -0.18),
    yellow: roundedBox(0.22, 0.22, 0.08, 0.04).translate(0, 3.35, -0.18),
    green: roundedBox(0.22, 0.22, 0.08, 0.04).translate(0, 3.02, -0.18),
  };
  readonly #localLights: THREE.SpotLight[];
  readonly #transform = new THREE.Object3D();
  readonly #lastPlayerPosition = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    0,
    Number.POSITIVE_INFINITY,
  );
  #epochSeconds = 0;
  #artificialLight = 0;
  #localLightsDirty = true;
  #activeLocalLights = 0;
  #shadowBudget = 0;
  #disposed = false;

  static async loadManifest(): Promise<CityLightingManifest> {
    const response = await fetch("/world/lighting-manifest.msgpack", {
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error(`City lighting manifest failed to load: ${response.status}`);
    const manifest = decode(new Uint8Array(await response.arrayBuffer()));
    validateCityLightingManifest(manifest);
    return manifest;
  }

  constructor(scene: THREE.Scene, manifest: CityLightingManifest) {
    validateCityLightingManifest(manifest);
    this.#entries = new Map(manifest.tiles.map((entry) => [entry.id, entry]));
    const active = {} as Record<VehicleAspect, THREE.MeshStandardMaterial>;
    const inactive = {} as Record<VehicleAspect, THREE.MeshStandardMaterial>;
    for (const aspect of ASPECTS) {
      active[aspect] = new THREE.MeshStandardMaterial({
        color: SIGNAL_COLORS[aspect],
        emissive: SIGNAL_COLORS[aspect],
        emissiveIntensity: 2.6,
        roughness: 0.45,
        metalness: 0,
      });
      inactive[aspect] = new THREE.MeshStandardMaterial({
        color: SIGNAL_COLORS[aspect],
        emissive: SIGNAL_COLORS[aspect],
        emissiveIntensity: 0.04,
        roughness: 0.62,
        metalness: 0,
      });
    }
    this.#signalMaterials = { active, inactive };
    this.#localLights = Array.from({ length: MAX_LOCAL_STREET_LIGHTS }, (_, index) => {
      const light = new THREE.SpotLight(
        0xffd69a,
        0,
        LOCAL_LIGHT_RANGE_M,
        THREE.MathUtils.degToRad(68),
        0.55,
        2,
      );
      light.name = `City street light ${index + 1}`;
      light.target.name = `City street light ${index + 1} target`;
      light.castShadow = false;
      light.shadow.autoUpdate = false;
      light.shadow.mapSize.set(256, 256);
      light.shadow.camera.near = 0.5;
      light.shadow.camera.far = LOCAL_LIGHT_RANGE_M;
      light.shadow.bias = -0.0005;
      light.shadow.normalBias = 0.04;
      light.visible = false;
      scene.add(light, light.target);
      return light;
    });
  }

  setShadowBudget(count: number): void {
    const budget = THREE.MathUtils.clamp(Math.trunc(count), 0, this.#localLights.length);
    for (let index = 0; index < this.#localLights.length; index += 1) {
      const light = this.#localLights[index]!;
      const enabled = index < budget;
      if (enabled && !light.castShadow) light.shadow.needsUpdate = true;
      light.castShadow = enabled;
    }
    this.#shadowBudget = budget;
  }

  async prepareTile(
    id: string,
    generation: number,
    signal: AbortSignal,
  ): Promise<PreparedLightingTile | null> {
    if (this.#disposed) throw new Error("CityLightingSystem is disposed");
    signal.throwIfAborted();
    const key = `${generation}:${id}`;
    const existing = this.#preparedTiles.get(key);
    if (existing) return existing.handle;
    const entry = this.#entries.get(id);
    if (!entry) return null;
    const response = await fetch(`/world/${entry.url}`, {
      credentials: "same-origin",
      signal,
    });
    if (!response.ok) throw new Error(`Lighting tile ${id} failed to load: ${response.status}`);
    const decoded = decodeCityLightingTile(
      decode(new Uint8Array(await response.arrayBuffer())),
      id,
    );
    signal.throwIfAborted();
    const tile = this.#buildTile(decoded, entry);
    try {
      signal.throwIfAborted();
      const handle: PreparedLightingTile = { id, generation, root: tile.root };
      this.#preparedTiles.set(key, { handle, tile });
      return handle;
    } catch (error) {
      tile.root.clear();
      throw error;
    }
  }

  commitPreparedTile(prepared: PreparedLightingTile): void {
    const key = `${prepared.generation}:${prepared.id}`;
    const record = this.#preparedTiles.get(key);
    if (!record || record.handle !== prepared)
      throw new Error(`Lighting tile ${key} is not prepared by this system`);
    if (this.#tiles.has(prepared.id))
      throw new Error(`Lighting tile ${prepared.id} is already active`);
    this.#preparedTiles.delete(key);
    this.#tiles.set(prepared.id, record.tile);
    this.#localLightsDirty = true;
    this.#updateSignalTile(record.tile);
  }

  disposePreparedTile(prepared: PreparedLightingTile): void {
    const key = `${prepared.generation}:${prepared.id}`;
    const record = this.#preparedTiles.get(key);
    if (!record || record.handle !== prepared) return;
    this.#preparedTiles.delete(key);
    record.tile.root.removeFromParent();
    record.tile.root.clear();
  }

  retireTile(id: string): RetiredLightingTile | null {
    const tile = this.#tiles.get(id);
    if (!tile) return null;
    this.#tiles.delete(id);
    tile.root.removeFromParent();
    const retired: RetiredLightingTile = { id };
    this.#retiredTiles.set(retired, tile);
    this.#localLightsDirty = true;
    this.#hideLocalLights();
    return retired;
  }

  disposeRetiredTile(retired: RetiredLightingTile): void {
    const tile = this.#retiredTiles.get(retired);
    if (!tile) return;
    this.#retiredTiles.delete(retired);
    tile.root.removeFromParent();
    tile.root.clear();
  }

  update(epochSeconds: number, artificialLight: number): void {
    if (this.#disposed) return;
    this.#epochSeconds = epochSeconds;
    const nextArtificialLight = THREE.MathUtils.clamp(artificialLight, 0, 1);
    if (nextArtificialLight !== this.#artificialLight) this.#localLightsDirty = true;
    this.#artificialLight = nextArtificialLight;
    this.#lampMaterial.emissiveIntensity = 2.8 * nextArtificialLight;
    for (const tile of this.#tiles.values()) this.#updateSignalTile(tile);
    if (nextArtificialLight === 0) this.#hideLocalLights();
  }

  frameUpdate(playerPosition: THREE.Vector3): void {
    if (this.#disposed) return;
    const movedEnough =
      this.#lastPlayerPosition.distanceToSquared(playerPosition) >=
      LOCAL_LIGHT_REFRESH_DISTANCE_M * LOCAL_LIGHT_REFRESH_DISTANCE_M;
    if (!this.#localLightsDirty && !movedEnough) return;
    this.#lastPlayerPosition.copy(playerPosition);
    this.#localLightsDirty = false;
    if (this.#artificialLight === 0) {
      this.#hideLocalLights();
      return;
    }
    const nearest: { light: StreetLightRecord; distanceSquared: number }[] = [];
    const radiusSquared = LOCAL_LIGHT_SELECTION_RADIUS_M * LOCAL_LIGHT_SELECTION_RADIUS_M;
    for (const tile of this.#tiles.values()) {
      for (const light of tile.streetLights) {
        const distanceSquared =
          (light.x - playerPosition.x) ** 2 + (light.z - playerPosition.z) ** 2;
        if (distanceSquared > radiusSquared) continue;
        let insertion = nearest.length;
        while (insertion > 0 && nearest[insertion - 1]!.distanceSquared > distanceSquared)
          insertion -= 1;
        if (insertion >= MAX_LOCAL_STREET_LIGHTS) continue;
        nearest.splice(insertion, 0, { light, distanceSquared });
        if (nearest.length > MAX_LOCAL_STREET_LIGHTS) nearest.pop();
      }
    }
    this.#activeLocalLights = nearest.length;
    for (let index = 0; index < this.#localLights.length; index += 1) {
      const spotLight = this.#localLights[index]!;
      const selected = nearest[index]?.light;
      const rendered = selected && index < MAX_RENDERED_LOCAL_STREET_LIGHTS;
      spotLight.visible = Boolean(rendered);
      spotLight.intensity = rendered ? 420 * this.#artificialLight : 0;
      if (rendered) {
        const sourceX = selected.x + Math.cos(selected.yawRadians) * 1.1;
        const sourceZ = selected.z - Math.sin(selected.yawRadians) * 1.1;
        spotLight.position.set(
          sourceX,
          (STREET_LIGHT_BASE_HEIGHT_M - 0.33) * (selected.heightM / STREET_LIGHT_BASE_HEIGHT_M),
          sourceZ,
        );
        spotLight.target.position.set(sourceX, 0, sourceZ);
        if (spotLight.castShadow) spotLight.shadow.needsUpdate = true;
      }
    }
  }

  snapshot(): CityLightingSnapshot {
    const snapshot: CityLightingSnapshot = {
      artificialLight: this.#artificialLight,
      streetLights: 0,
      activeLocalLights: this.#activeLocalLights,
      shadowCastingStreetLights: Math.min(this.#activeLocalLights, this.#shadowBudget),
      trafficSignals: 0,
      signalAspects: { red: 0, yellow: 0, green: 0 },
      batches: 0,
    };
    for (const tile of this.#tiles.values()) {
      snapshot.streetLights += tile.streetLights.length;
      snapshot.trafficSignals += tile.trafficSignals.length;
      snapshot.batches += tile.batches;
      for (const signal of tile.trafficSignals)
        snapshot.signalAspects[
          signalState(
            this.#epochSeconds,
            signal.x,
            signal.z,
            signal.axis,
            signal.crosswalkLengthM,
          ).vehicle
        ] += 1;
    }
    return snapshot;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const tile of this.#tiles.values()) tile.root.removeFromParent();
    this.#tiles.clear();
    for (const { tile } of this.#preparedTiles.values()) {
      tile.root.removeFromParent();
      tile.root.clear();
    }
    this.#preparedTiles.clear();
    for (const tile of this.#retiredTiles.values()) {
      tile.root.removeFromParent();
      tile.root.clear();
    }
    this.#retiredTiles.clear();
    for (const light of this.#localLights) {
      light.removeFromParent();
      light.target.removeFromParent();
      light.visible = false;
      light.intensity = 0;
      light.shadow.dispose();
    }
    this.#streetFrameGeometry.dispose();
    this.#lampLensGeometry.dispose();
    this.#signalFrameGeometry.dispose();
    for (const geometry of Object.values(this.#signalLensGeometries)) geometry.dispose();
    this.#frameMaterial.dispose();
    this.#lampMaterial.dispose();
    for (const material of Object.values(this.#signalMaterials.active)) material.dispose();
    for (const material of Object.values(this.#signalMaterials.inactive)) material.dispose();
    this.#activeLocalLights = 0;
  }

  #buildTile(decoded: DecodedCityLightingTile, entry: CityLightingTileIndexEntry): LightingTile {
    const root = new THREE.Group();
    root.name = `city-lighting:${entry.id}`;
    const streetLights: StreetLightRecord[] = [];
    for (let offset = 0; offset < decoded.streetLights.length; offset += STREET_LIGHT_STRIDE)
      streetLights.push({
        x: decoded.streetLights[offset]!,
        z: decoded.streetLights[offset + 1]!,
        yawRadians: decoded.streetLights[offset + 2]!,
        heightM: decoded.streetLights[offset + 3]!,
      });
    const trafficSignals: TrafficSignalRecord[] = [];
    for (let offset = 0; offset < decoded.trafficSignals.length; offset += TRAFFIC_SIGNAL_STRIDE)
      trafficSignals.push({
        x: decoded.trafficSignals[offset]!,
        z: decoded.trafficSignals[offset + 1]!,
        yawRadians: decoded.trafficSignals[offset + 2]!,
        axis: decoded.trafficSignals[offset + 3]! as 0 | 1,
        crosswalkLengthM: decoded.trafficSignals[offset + 4]!,
      });
    if (
      streetLights.length !== entry.streetLightCount ||
      trafficSignals.length !== entry.trafficSignalCount
    )
      throw new Error(`Lighting tile ${entry.id} count differs from its manifest`);

    let batches = 0;
    if (streetLights.length > 0) {
      const frames = new THREE.InstancedMesh(
        this.#streetFrameGeometry,
        this.#frameMaterial,
        streetLights.length,
      );
      const lenses = new THREE.InstancedMesh(
        this.#lampLensGeometry,
        this.#lampMaterial,
        streetLights.length,
      );
      frames.name = "Street-light frames";
      lenses.name = "Street-light lenses";
      frames.castShadow = true;
      frames.receiveShadow = true;
      lenses.castShadow = false;
      lenses.receiveShadow = false;
      for (let index = 0; index < streetLights.length; index += 1) {
        const light = streetLights[index]!;
        const scaleY = light.heightM / STREET_LIGHT_BASE_HEIGHT_M;
        setTransform(frames, index, this.#transform, light.x, light.z, light.yawRadians, scaleY);
        setTransform(lenses, index, this.#transform, light.x, light.z, light.yawRadians, scaleY);
      }
      finishInstances(frames);
      finishInstances(lenses);
      root.add(frames, lenses);
      batches += 2;
    }

    let signalMeshes: SignalMeshes | undefined;
    if (trafficSignals.length > 0) {
      const frames = new THREE.InstancedMesh(
        this.#signalFrameGeometry,
        this.#frameMaterial,
        trafficSignals.length,
      );
      frames.name = "Traffic-signal frames";
      frames.castShadow = true;
      frames.receiveShadow = true;
      for (let index = 0; index < trafficSignals.length; index += 1) {
        const signal = trafficSignals[index]!;
        setTransform(frames, index, this.#transform, signal.x, signal.z, signal.yawRadians);
      }
      finishInstances(frames);
      root.add(frames);
      const active = {} as Record<VehicleAspect, THREE.InstancedMesh>;
      const inactive = {} as Record<VehicleAspect, THREE.InstancedMesh>;
      for (const aspect of ASPECTS) {
        active[aspect] = new THREE.InstancedMesh(
          this.#signalLensGeometries[aspect],
          this.#signalMaterials.active[aspect],
          trafficSignals.length,
        );
        inactive[aspect] = new THREE.InstancedMesh(
          this.#signalLensGeometries[aspect],
          this.#signalMaterials.inactive[aspect],
          trafficSignals.length,
        );
        active[aspect].name = `Traffic-signal ${aspect} active`;
        inactive[aspect].name = `Traffic-signal ${aspect} inactive`;
        active[aspect].castShadow = false;
        active[aspect].receiveShadow = false;
        inactive[aspect].castShadow = false;
        inactive[aspect].receiveShadow = false;
        root.add(active[aspect], inactive[aspect]);
      }
      signalMeshes = { active, inactive };
      batches += 7;
    }
    if (batches > MAX_LIGHTING_BATCHES_PER_TILE)
      throw new Error(`Lighting tile ${entry.id} exceeded its batch budget`);
    return { root, streetLights, trafficSignals, signalMeshes, batches };
  }

  #updateSignalTile(tile: LightingTile): void {
    const meshes = tile.signalMeshes;
    if (!meshes) return;
    const activeCounts: Record<VehicleAspect, number> = { red: 0, yellow: 0, green: 0 };
    const inactiveCounts: Record<VehicleAspect, number> = { red: 0, yellow: 0, green: 0 };
    for (const signal of tile.trafficSignals) {
      const activeAspect = signalState(
        this.#epochSeconds,
        signal.x,
        signal.z,
        signal.axis,
        signal.crosswalkLengthM,
      ).vehicle;
      for (const aspect of ASPECTS) {
        const bucket = aspect === activeAspect ? meshes.active[aspect] : meshes.inactive[aspect];
        const counts = aspect === activeAspect ? activeCounts : inactiveCounts;
        setTransform(
          bucket,
          counts[aspect],
          this.#transform,
          signal.x,
          signal.z,
          signal.yawRadians,
        );
        counts[aspect] += 1;
      }
    }
    for (const aspect of ASPECTS) {
      meshes.active[aspect].count = activeCounts[aspect];
      meshes.inactive[aspect].count = inactiveCounts[aspect];
      meshes.active[aspect].instanceMatrix.needsUpdate = true;
      meshes.inactive[aspect].instanceMatrix.needsUpdate = true;
    }
  }

  #hideLocalLights(): void {
    this.#activeLocalLights = 0;
    for (const light of this.#localLights) {
      light.visible = false;
      light.intensity = 0;
    }
  }
}
